import axios from "axios";
import https from "https";
import {
  signOutUser,
  getFreshAccessToken,
  isLoginRequiredError,
} from "../auth/workosAuth";

const axiosInstance = axios.create();
const httpsAgent = new https.Agent({ rejectUnauthorized: false });
axios.defaults.httpsAgent = httpsAgent;

// On a 401 we attempt ONE silent token refresh + retry before logging out.
// WorkOS access tokens are short-lived (~5 min), so a request can race a
// not-yet-completed background refresh (e.g. a tab waking from sleep) and send a
// momentarily-expired token. The previous behavior logged users out on the first
// 401, which ejected them constantly under the short token lifetime. Instead we
// force-refresh the token, replay the original request once, and only sign the
// user out when the session genuinely can't be refreshed (or the replay still
// 401s). Exported for unit testing; deps are injectable so tests don't touch the
// network or the real AuthKit client.
export async function handleResponseError(error, deps = {}) {
  const {
    refresh = getFreshAccessToken,
    signOut = signOutUser,
    // Whether a refresh failure is terminal (session unrecoverable) vs transient.
    isTerminal = isLoginRequiredError,
    // Replay via bare axios (no interceptors) to avoid re-entering this handler.
    retry = axios,
  } = deps;

  const originalRequest = error.config;
  const status = error.response && error.response.status;

  if (status === 401 && originalRequest && !originalRequest._retry) {
    originalRequest._retry = true;

    let token;
    try {
      token = await refresh();
    } catch (refreshError) {
      // Only sign out when the session is genuinely unrecoverable (inactivity /
      // max-session reached, or the refresh token was revoked). Transient
      // failures -- a network blip, or a 401 that arrives before AuthKit has
      // initialized -- must NOT clear the session; reject and let the caller or
      // the next request recover.
      if (isTerminal(refreshError)) {
        signOut();
      }
      return Promise.reject(refreshError);
    }

    originalRequest.headers = originalRequest.headers || {};
    originalRequest.headers["Authorization"] = `Bearer ${token}`;

    try {
      return await retry(originalRequest);
    } catch (retryError) {
      if (retryError && retryError.response && retryError.response.status === 401) {
        // Fresh token still rejected -> the session is effectively dead.
        signOut();
      }
      return Promise.reject(
        (retryError && retryError.response && retryError.response.data) ||
          "Something went wrong"
      );
    }
  }

  return Promise.reject(
    (error.response && error.response.data) || "Something went wrong"
  );
}

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => handleResponseError(error)
);

axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      config.headers["Authorization"] = "Bearer " + token;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export default axiosInstance;
